import React, {
  ChangeEvent,
  forwardRef,
  ReactElement,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import firebase from 'shared/lib/firebase';
import Tooltip from 'shared/components/Tooltip';
import useClinicUser from 'shared/hooks/useClinicUser';
import { cleanPhoneNumber, formatPhoneNumber } from 'shared/utils';
import ChangePasswordModal from './ChangePasswordModal';
import {
  UserSettingsDataFragment,
  useUpdateClinicSettingsUserMutation,
  UserType,
  useGetClinicSettingsUserQuery,
} from 'shared/types/graphql';
import ChangeEmailModal from './ChangeEmailModal';
import { ImageData } from 'pages/Settings/types/ImageData';
import CropImageModal from '../../components/CropImageModal';
import { GeneralInfoComponent } from '../GeneralInfoSettings';
import { Button, Text, TextInput, Icon, Flex, HStack, Divider, useDisclosure, Select, Stack } from '@televet/kibble-ui';
import { STATUSES, useCustomToast } from 'shared/hooks/useCustomToast';
import { useValidation, ValidationResponseCodes } from '../../../../shared/hooks/useValidation';
import useDebounce from 'shared/hooks/useDebounce';
import { ClinicAvatar } from 'shared/components/Avatars';
import { useTracking } from 'shared/providers/TrackingProvider';
import { EventName, SettingsMyProfileEvents } from 'shared/providers/TrackingProvider/EventNames';
import { Dict } from 'pages/Reporting/QuickSightReports/embedDashboard';
import { useResolutionProvider } from 'shared/providers/ResolutionProvider';

enum ProfileChangeField {
  Prefixes = 'namePrefix',
  First_Name = 'firstName',
  Last_Name = 'lastName',
  Suffix = 'nameSuffix',
  Display_Name = 'nameDisplay',
  Phone_Number = 'phone',
}

const prefixes = ['Mr', 'Mrs', 'Ms', 'Dr'];

const emptyUser: UserSettingsDataFragment = {
  id: '',
  profilePic: '',
  namePrefix: '',
  firstName: '',
  lastName: '',
  nameSuffix: '',
  nameDisplay: '',
  displayName: '',
  email: '',
  phone: '',
  firebaseId: '',
  vetInfo: {
    id: '',
  },
};

const NAME_PATTERN = new RegExp(/^$|^([a-zà-ž][-,a-zà-ž.']*)$/, 'i');
const DISPLAY_NAME_PATTERN = new RegExp(/^$|^([a-zà-ž][-,a-zà-ž. ']*)+$/, 'i');

const MyProfileSettings = forwardRef(
  (
    {
      setHasUnsavedChange,
      setIsSettingsChangesValid,
    }: {
      setHasUnsavedChange: React.Dispatch<React.SetStateAction<boolean>>;
      setIsSettingsChangesValid: React.Dispatch<React.SetStateAction<boolean>>;
    },
    ref,
  ): ReactElement => {
    // Custom hook and provider
    const { clinicUser } = useClinicUser();
    const { validatePhoneNumber } = useValidation();
    const { trackEvent } = useTracking();
    const { isMobile } = useResolutionProvider();

    // Constants and refs
    const clinicUserId = clinicUser?.id;
    const hiddenFileInputRef = useRef<HTMLInputElement | null>(null);
    const type = UserType.Vet;

    // Modal
    const { isOpen: isCropImageModalOpen, onOpen: openCropImageModal, onClose: closeCropImageModal } = useDisclosure();
    const {
      isOpen: isChangeEmailModalOpen,
      onClose: closeChangeEmailModal,
      onOpen: openChangeEmailModal,
    } = useDisclosure();
    const {
      isOpen: isChangePasswordModalOpen,
      onClose: closeChangePasswordModal,
      onOpen: openChangePasswordModal,
    } = useDisclosure();
    const { toastMessagePop } = useCustomToast();

    // State
    const [userProfile, setUserProfile] = useState<UserSettingsDataFragment>(emptyUser);
    const [imageData, setImageData] = useState<ImageData>();
    const [phoneValue, setPhoneValue] = useState('');
    const [phoneValidationErrorMessage, setPhoneValidationErrorMessage] = useState('');
    const [profilePageChanges, setProfilePageChanges] = useState<ProfileChangeField[]>();
    const [profilePageError, setProfilePageError] = useState<ProfileChangeField[]>([]);

    //Apollo
    const [updateUser] = useUpdateClinicSettingsUserMutation();
    const { data: userData } = useGetClinicSettingsUserQuery({
      variables: { where: { id: clinicUserId } },
      skip: !clinicUserId,
    });

    const uniqueUserProfile = userData?.findUniqueUser;

    const [debouncedPhoneValue] = useDebounce(phoneValue, 350);

    const handleFileInputChange = (e: ChangeEvent<HTMLInputElement>): void => {
      const files = e?.target?.files;

      if (files?.length) {
        const { name, size } = files[0];
        setImageData({ name, size: size / 1024 / 1024, file: files[0] });
        openCropImageModal();
      }
    };

    const handleUpdateImage = async (blob: Blob): Promise<void> => {
      const storageRef = firebase.storage().ref(`users/${userProfile.firebaseId}`);
      const snapshot = await storageRef.put(blob);
      const newImageURL = await snapshot.ref.getDownloadURL();
      try {
        await updateUser({ variables: { where: { id: clinicUserId }, data: { profilePic: newImageURL, type } } });
        toastMessagePop();
      } catch (error) {}
    };

    const setInitialInfoData = useCallback(() => {
      if (uniqueUserProfile) {
        setUserProfile(uniqueUserProfile);
      }
      setProfilePageChanges([]);
      setHasUnsavedChange(false);
      setPhoneValue('');
      setPhoneValidationErrorMessage('');
      setProfilePageError([]);
      setIsSettingsChangesValid(true);
    }, [setHasUnsavedChange, setIsSettingsChangesValid, uniqueUserProfile]);

    const onRemovingProfilePageChangeError = useCallback(
      (field: ProfileChangeField) => {
        const newProfilePageError = [...profilePageError];

        const newState = newProfilePageError.filter((err) => err !== field);
        setProfilePageError(newState);
        if (newState.length === 0) {
          setIsSettingsChangesValid(true);
        }
      },
      [profilePageError, setIsSettingsChangesValid],
    );

    const onAddingProfilePageChangeError = useCallback(
      (field: ProfileChangeField) => {
        const newProfilePageError = [...profilePageError];
        newProfilePageError.push(field);
        setProfilePageError(newProfilePageError);
        setIsSettingsChangesValid(false);
      },
      [profilePageError, setIsSettingsChangesValid],
    );

    const onValidatingName = (field: ProfileChangeField, value: string): void => {
      const patternSelected = field === ProfileChangeField.Display_Name ? DISPLAY_NAME_PATTERN : NAME_PATTERN;
      const isValidName = patternSelected.test(value.trim());

      if (!isValidName && !profilePageError.includes(field)) {
        onAddingProfilePageChangeError(field);
      } else if (isValidName && profilePageError.includes(field)) {
        onRemovingProfilePageChangeError(field);
      }
    };

    const onProfileChange = (field: ProfileChangeField, value: string, trackMessage: EventName, props?: Dict): void => {
      const newProfileState = { ...userProfile };
      const newProfilePageChanges = [...(profilePageChanges || [])];
      newProfileState[field] = value;
      setUserProfile(newProfileState);
      trackEvent(trackMessage, props);

      const valueToCheck = field === ProfileChangeField.Phone_Number ? cleanPhoneNumber(value) : value;

      if (uniqueUserProfile && uniqueUserProfile[field] !== valueToCheck) {
        setHasUnsavedChange(true);
        if (!newProfilePageChanges.includes(field)) {
          newProfilePageChanges.push(field);
        }
        setProfilePageChanges(newProfilePageChanges);
      } else if (uniqueUserProfile && uniqueUserProfile[field] === valueToCheck) {
        const newState = profilePageChanges?.filter((profilePageChange) => profilePageChange !== field);
        setProfilePageChanges(newState);
        if (newState?.length === 0) {
          setHasUnsavedChange(false);
        }
      }
    };

    const onValidatingPhoneNumber = useCallback(
      async (phonenumber: string): Promise<void> => {
        const phone = cleanPhoneNumber(phonenumber || '');
        const { isValid, code } = await validatePhoneNumber(phone);

        if (!isValid && !profilePageError.includes(ProfileChangeField.Phone_Number)) {
          onAddingProfilePageChangeError(ProfileChangeField.Phone_Number);
        } else if (isValid && profilePageError.includes(ProfileChangeField.Phone_Number)) {
          onRemovingProfilePageChangeError(ProfileChangeField.Phone_Number);
        } else if (code === ValidationResponseCodes.PhoneNumberInUse) {
          setPhoneValidationErrorMessage('This phone number is already in use.');
        } else if (phone.length !== 10) {
          setPhoneValidationErrorMessage('Phone number is of invalid length');
        } else if (phone.length === 10 && phone[0] === '0') {
          setPhoneValidationErrorMessage('Please provide a valid phone number');
        }
      },
      [validatePhoneNumber, profilePageError, onAddingProfilePageChangeError, onRemovingProfilePageChangeError],
    );

    const onSaveProfilePageChanges = (): void => {
      if (!!profilePageChanges && profilePageChanges.length > 0) {
        if (userProfile.nameDisplay === '') {
          userProfile.nameDisplay = `${userProfile.firstName} ${userProfile.lastName}`;
        }
        const { namePrefix, nameSuffix, firstName, lastName, nameDisplay, phone } = userProfile;
        onUpdate({ namePrefix, nameSuffix, firstName, lastName, nameDisplay, phone });
      }
    };

    const onUpdate = useCallback(
      async (data) => {
        if (clinicUserId) {
          try {
            await updateUser({ variables: { where: { id: clinicUserId }, data: { ...data, type } } });

            toastMessagePop();
            setProfilePageChanges([]);
          } catch (e) {
            console.error(e);
            toastMessagePop('Your clinic was unable to be updated. Please try again.', STATUSES.error);
          }
        }
      },
      [clinicUserId, updateUser, type, toastMessagePop],
    );

    // forwarded function
    useImperativeHandle(ref, () => ({
      onLeavingCurrentPage(): void {
        setProfilePageChanges([]);
      },
      onClickedSaveButton(): void {
        onSaveProfilePageChanges();
      },
      onClickedCancelButton(): void {
        setInitialInfoData();
      },
    }));

    const prefixOptions = useMemo(() => {
      const _prefixes = prefixes.map((prefix) => ({
        value: prefix,
        label: prefix,
        isSelected: userProfile.namePrefix === prefix,
      }));

      return [{ label: '', value: 'empty' }, ..._prefixes];
    }, [userProfile.namePrefix]);

    // Effects
    useEffect(() => {
      setInitialInfoData();
    }, [setInitialInfoData]);

    useEffect(() => {
      if (!debouncedPhoneValue && profilePageError.includes(ProfileChangeField.Phone_Number)) {
        onRemovingProfilePageChangeError(ProfileChangeField.Phone_Number);
        setPhoneValidationErrorMessage('');
      }
      if (debouncedPhoneValue !== '') {
        onValidatingPhoneNumber(debouncedPhoneValue);
      }
    }, [debouncedPhoneValue, onRemovingProfilePageChangeError, onValidatingPhoneNumber, profilePageError]);

    return (
      <>
        {imageData?.file && (
          <CropImageModal
            fileUrl={imageData ? URL.createObjectURL(imageData.file) : ''}
            onApplyCrop={handleUpdateImage}
            isOpen={isCropImageModalOpen}
            onClose={closeCropImageModal}
            setImageData={(): void => setImageData(undefined)}
          />
        )}
        <ChangeEmailModal updateUser={updateUser} isOpen={isChangeEmailModalOpen} onClose={closeChangeEmailModal} />
        <ChangePasswordModal
          email={userProfile.email}
          isOpen={isChangePasswordModalOpen}
          onClose={closeChangePasswordModal}
        />
        <GeneralInfoComponent spacing={3.5} margin="20px 0px" title="Your Avatar">
          <HStack spacing={3.5} boxSize="70px" w="100%">
            <ClinicAvatar tooltipText="User Profile Pic" clinicMember={userProfile} size="xl" />

            <Button
              variant="secondary"
              size="xs"
              iconPosition="right"
              onClick={(): void => {
                if (hiddenFileInputRef && hiddenFileInputRef.current) {
                  hiddenFileInputRef.current.click();
                  trackEvent(SettingsMyProfileEvents.EditUserAvatar);
                }
              }}
            >
              Choose File
            </Button>
          </HStack>
          <Text size="sm" variant="placeholder">
            Must be a .jpg or .png file smaller than 5 MB and at least 400 by 400.
          </Text>
          <TextInput
            className="HiddenFileInput"
            onChange={handleFileInputChange}
            onClick={(e): void => {
              e.currentTarget.value = '';
            }}
            ref={hiddenFileInputRef}
            type="file"
            accept="image/*"
            display="none"
            m={0}
          />
        </GeneralInfoComponent>
        <Divider borderColor="divider.default" mt={6} />
        <GeneralInfoComponent spacing={9} margin="20px 0px" title="Contact Information">
          <Flex direction={isMobile ? 'column' : 'row'} mt="10px !important" rowGap={4} columnGap={6}>
            <GeneralInfoComponent spacing={1.5} subtitle="Prefix" m={0}>
              <Select
                size="sm"
                width="100px"
                value={userProfile.namePrefix || uniqueUserProfile?.namePrefix || ''}
                options={prefixOptions}
                listProps={{
                  onSelect: (selectedItem): void => {
                    const _value = selectedItem?.value === 'empty' ? '' : selectedItem?.value;
                    onProfileChange(ProfileChangeField.Prefixes, _value || '', SettingsMyProfileEvents.EditUserPrefix, {
                      PrefixType: _value,
                    });
                  },
                }}
              />
            </GeneralInfoComponent>
            <GeneralInfoComponent spacing={1.5} subtitle="First Name" m={0}>
              <TextInput
                w={177}
                value={userProfile.firstName}
                placeholder={userProfile.firstName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
                  onValidatingName(ProfileChangeField.First_Name, e.target.value);
                  onProfileChange(
                    ProfileChangeField.First_Name,
                    e.target.value,
                    SettingsMyProfileEvents.EditUserFirstName,
                  );
                }}
                isInvalid={profilePageError.includes(ProfileChangeField.First_Name)}
                errorText="Contains invalid character"
              />
            </GeneralInfoComponent>
            <GeneralInfoComponent spacing={1.5} subtitle="Last Name" m={0}>
              <TextInput
                w={177}
                value={userProfile.lastName}
                placeholder={userProfile.lastName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
                  onValidatingName(ProfileChangeField.Last_Name, e.target.value);
                  onProfileChange(
                    ProfileChangeField.Last_Name,
                    e.target.value,
                    SettingsMyProfileEvents.EditUserLastName,
                  );
                }}
                isInvalid={profilePageError.includes(ProfileChangeField.Last_Name)}
                errorText="Contains invalid character"
              />
            </GeneralInfoComponent>
            <GeneralInfoComponent spacing={1.5} subtitle="Suffix" m={0}>
              <TextInput
                w={177}
                value={userProfile.nameSuffix || ''}
                placeholder={userProfile.nameSuffix || ''}
                onChange={(e: React.ChangeEvent<HTMLInputElement>): void =>
                  onProfileChange(ProfileChangeField.Suffix, e.target.value, SettingsMyProfileEvents.EditUserSuffix)
                }
              />
            </GeneralInfoComponent>
          </Flex>
          <GeneralInfoComponent
            spacing={2}
            subtitle="Display Name"
            toolTip={
              <Tooltip content="This name will appear to your clients and throughout the application.">
                <Icon name="infoCircle" size="xs" />
              </Tooltip>
            }
          >
            <TextInput
              w={475}
              value={userProfile.nameDisplay || ''}
              placeholder="Display Name"
              onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
                onValidatingName(ProfileChangeField.Display_Name, e.target.value);
                onProfileChange(
                  ProfileChangeField.Display_Name,
                  e.target.value,
                  SettingsMyProfileEvents.EditUserDisplayName,
                );
              }}
              isInvalid={profilePageError.includes(ProfileChangeField.Display_Name)}
              errorText="Contains invalid character"
            />
          </GeneralInfoComponent>
          <GeneralInfoComponent spacing={2} subtitle="Email Address">
            <Stack direction={isMobile ? 'column' : 'row'} spacing={3} justify="flex-start" align="flex-start">
              <TextInput maxW={375} value={userProfile.email} isDisabled={true} _placeholder={{ color: '#060a3a' }} />
              <Button
                variant="secondary"
                onClick={(): void => {
                  openChangeEmailModal();
                  trackEvent(SettingsMyProfileEvents.ViewEditEmailModal);
                }}
              >
                Change Email
              </Button>
            </Stack>
          </GeneralInfoComponent>
          <GeneralInfoComponent spacing={2} subtitle="Phone Number">
            <TextInput
              w={163}
              textAlign="center"
              placeholder={formatPhoneNumber(userProfile.phone || '')}
              value={phoneValue || formatPhoneNumber(userProfile.phone || '')}
              onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
                setPhoneValue(formatPhoneNumber(e.target.value));
                onProfileChange(ProfileChangeField.Phone_Number, e.target.value, SettingsMyProfileEvents.EditUserPhone);
              }}
              isInvalid={profilePageError.includes(ProfileChangeField.Phone_Number)}
              errorText={phoneValidationErrorMessage}
            />
          </GeneralInfoComponent>
        </GeneralInfoComponent>
        <Divider borderColor="divider.default" mt={5} />
        <GeneralInfoComponent margin="20px 0px" spacing={2} title="Password">
          <Button
            width="max-content"
            variant="secondary"
            onClick={(): void => {
              trackEvent(SettingsMyProfileEvents.ViewEditPasswordModal);
              openChangePasswordModal();
            }}
          >
            Change Password
          </Button>
        </GeneralInfoComponent>
      </>
    );
  },
);

export default MyProfileSettings;
